import type { UserRole } from '@coreltb/shared/constants';
import {
  BarChart3,
  Bell,
  Briefcase,
  ClipboardCheck,
  FileSignature,
  FileText,
  Images,
  Inbox,
  KanbanSquare,
  Megaphone,
  MessageSquare,
  Settings,
  TrendingUp,
  Wrench,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/**
 * JEDNA lista nawigacji dla sidebaru (desktop) i menu mobilnego. Wczesniej
 * menu mobilne mialo wlasna kopie, ktora sie rozjechala (audyt 2026-09-29:
 * linki do nieistniejacych /dashboard i /koszyk-leadow, brak Projektow).
 */
export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Brak = widoczne dla wszystkich. Developer widzi wszystko (catch-all). */
  visibleFor?: readonly UserRole[];
  section: 'main' | 'admin';
  badge?: string;
  /** Licznik oczekujacych prosb (rabat + usuniecie oferty) obok pozycji. */
  pendingCounter?: 'approvals';
  /** Inne sciezki (zakladki sekcji), na ktorych pozycja tez jest podswietlona. */
  activeFor?: readonly string[];
}

export const NAV_ITEMS: readonly NavItem[] = [
  { to: '/crm', label: 'CRM', icon: KanbanSquare, section: 'main' },
  { to: '/crm/koszyk', label: 'Koszyk leadów', icon: Inbox, section: 'main' },
  {
    to: '/sprzedazy',
    label: 'Sprzedaż',
    icon: TrendingUp,
    visibleFor: ['admin', 'dyrektor_sprzedazy'],
    section: 'main',
  },
  {
    to: '/marketing',
    label: 'Marketing',
    icon: Megaphone,
    visibleFor: ['admin', 'marketing'],
    section: 'main',
  },
  {
    to: '/projekty',
    label: 'Projekty',
    icon: Briefcase,
    badge: 'beta',
    // Backend RBAC (canReadProject) i tak egzekwuje dostep per projekt.
    visibleFor: ['admin'],
    section: 'main',
  },
  {
    to: '/realizacje',
    label: 'Realizacje',
    icon: Images,
    // Lustro CAN_VIEW w API: handlowiec i dyrektor ogladaja, edytuje admin + marketing.
    visibleFor: ['admin', 'marketing', 'dyrektor_sprzedazy', 'handlowiec'],
    section: 'main',
  },
  {
    to: '/oferty',
    label: 'Oferty',
    icon: FileText,
    visibleFor: ['admin', 'dyrektor_sprzedazy', 'handlowiec'],
    section: 'main',
  },
  {
    to: '/umowy',
    label: 'Umowy',
    icon: FileSignature,
    visibleFor: ['admin', 'dyrektor_sprzedazy'],
    section: 'main',
  },
  {
    to: '/raporty',
    label: 'Raporty',
    icon: BarChart3,
    visibleFor: ['admin', 'dyrektor_sprzedazy'],
    section: 'main',
    activeFor: ['/analiza-leadow'],
  },
  { to: '/notyfikacje', label: 'Powiadomienia', icon: Bell, section: 'main' },
  // Administracja (Dawid 2026-10-01): 4 pozycje zamiast 9 — reszta to zakładki sekcji
  // (components/layout/SectionTabs.tsx), a obie prośby to jedna lista „Do akceptacji”.
  {
    to: '/do-akceptacji',
    label: 'Do akceptacji',
    icon: ClipboardCheck,
    visibleFor: ['admin', 'dyrektor_sprzedazy'],
    section: 'admin',
    pendingCounter: 'approvals',
  },
  {
    to: '/ustawienia',
    label: 'Ustawienia',
    icon: Settings,
    visibleFor: ['admin'],
    section: 'admin',
    activeFor: ['/ustawienia/cennik', '/ustawienia/szablony'],
  },
  {
    to: '/uwagi',
    label: 'Uwagi testerów',
    icon: MessageSquare,
    visibleFor: ['admin', 'dyrektor_sprzedazy', 'marketing', 'handlowiec'],
    section: 'admin',
  },
  {
    to: '/ustawienia/reminders-audit',
    label: 'Narzędzia',
    icon: Wrench,
    visibleFor: ['developer'],
    section: 'admin',
    activeFor: ['/ustawienia/uzycie', '/ustawienia/import'],
  },
];

/** Fail closed: bez zaladowanego usera widoczne tylko pozycje bez ograniczen. */
export function visibleNavItems(role: UserRole | undefined): NavItem[] {
  return NAV_ITEMS.filter((item) => {
    if (!item.visibleFor) return true;
    if (!role) return false;
    if (role === 'developer') return true;
    return item.visibleFor.includes(role);
  });
}

/** `end` (dokladne dopasowanie) gdy pozycja ma dziecko z dluzsza sciezka. */
export function isExactMatchItem(item: NavItem): boolean {
  return (
    item.to === '/crm' ||
    NAV_ITEMS.some((other) => other.to !== item.to && other.to.startsWith(`${item.to}/`))
  );
}
